import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, type Me } from '../../lib/api'
import { layout } from '../../design/tokens'

/**
 * Écran de premier passage : choisir son propre mot de passe.
 *
 * Il bloque le reste de l'application, et c'est le but. Le mot de passe posé par
 * l'administration a transité par un courrier, donc par une boîte que l'application ne maîtrise
 * pas : tant qu'il n'est pas remplacé, il faut le considérer comme connu d'un tiers. Le proposer
 * en option reviendrait à ce que personne ne le fasse.
 */
export function ChangementImpose({ me }: { me: Me }) {
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [afficher, setAfficher] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [fait, setFait] = useState(false)

  const changer = useMutation({
    mutationFn: () => api.changePassword(actuel, nouveau),
    // Le changement révoque toutes les sessions, celle-ci comprise : on ne recharge pas les
    // données, on demande une nouvelle connexion.
    onSuccess: () => setFait(true),
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le changement a échoué.'),
  })

  const tropCourt = nouveau.length > 0 && nouveau.length < 10
  const different = confirmation.length > 0 && confirmation !== nouveau

  if (fait) {
    return (
      <main
        className="mx-auto flex min-h-dvh flex-col justify-center bg-surface"
        style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
      >
        <h1 className="font-serif text-screen">C'est fait</h1>
        <p className="mt-3 text-body text-ink-muted">
          Votre mot de passe est changé. Reconnectez-vous avec.
        </p>
        <a
          href="/"
          className="mt-6 flex w-full items-center justify-center rounded-button bg-accent text-card-title font-semibold text-card"
          style={{ height: layout.primaryButtonHeight }}
        >
          Se connecter
        </a>
      </main>
    )
  }

  return (
    <main
      className="mx-auto flex min-h-dvh flex-col justify-center bg-surface"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter, paddingBlock: 32 }}
    >
      <h1 className="font-serif text-screen">Choisissez votre mot de passe</h1>
      <p className="mt-3 text-body text-ink-muted">
        Bonjour {me.displayName}. Celui qui vous a été envoyé est passé par un courrier : il faut
        le remplacer par un mot de passe que vous êtes seul à connaître.
      </p>

      <label className="mt-6 block text-label font-semibold uppercase text-ink-muted" htmlFor="actuel">
        Mot de passe reçu
      </label>
      <input
        id="actuel"
        type={afficher ? 'text' : 'password'}
        autoComplete="current-password"
        value={actuel}
        onChange={(evenement) => {
          setActuel(evenement.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="nouveau">
        Nouveau mot de passe
      </label>
      <input
        id="nouveau"
        type={afficher ? 'text' : 'password'}
        autoComplete="new-password"
        value={nouveau}
        onChange={(evenement) => {
          setNouveau(evenement.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />

      <label
        className="mt-4 block text-label font-semibold uppercase text-ink-muted"
        htmlFor="confirmation"
      >
        Répétez-le
      </label>
      {/* Une faute de frappe sur un champ unique enfermerait dehors : d'où la répétition, et le
          bouton pour voir ce qu'on tape. */}
      <input
        id="confirmation"
        type={afficher ? 'text' : 'password'}
        autoComplete="new-password"
        value={confirmation}
        onChange={(evenement) => {
          setConfirmation(evenement.target.value)
          setErreur(null)
        }}
        className="mt-2 w-full rounded-field border-[1.5px] border-border bg-card px-4 outline-none focus:border-accent"
        style={{ height: 56, fontSize: 17 }}
      />

      <button
        type="button"
        onClick={() => setAfficher((valeur) => !valeur)}
        className="mt-2 self-start text-meta font-semibold text-accent"
      >
        {afficher ? 'Masquer' : 'Afficher'}
      </button>

      {tropCourt ? (
        <p className="mt-3 text-meta text-ink-muted">Au moins 10 caractères.</p>
      ) : null}
      {different ? (
        <p className="mt-3 text-meta text-ink-muted">Les deux saisies diffèrent.</p>
      ) : null}
      {erreur ? <p className="mt-3 text-meta text-danger">{erreur}</p> : null}

      <button
        type="button"
        disabled={
          actuel.length === 0 ||
          nouveau.length < 10 ||
          confirmation !== nouveau ||
          changer.isPending
        }
        onClick={() => changer.mutate()}
        className="mt-6 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: layout.primaryButtonHeight }}
      >
        {changer.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </main>
  )
}
